import { FC, useEffect } from "react";
import { UseFormReturn, useFieldArray } from "react-hook-form";
import { makeStyles } from "tss-react/mui";
import {
  MiBox,
  MiButton,
  MiDetailFields,
  MiPlaceholderArea,
} from "@miplatform/components";
import {
  BUTTON_COLORS,
  BUTTON_VARIANTS,
  DISPATCH_EMAIL_TYPE,
  ENTITY_NAMES,
  INPUT_TYPES,
  PLATFORM_MODULES,
  RECIPIENT_TYPE_OPTIONS,
  SCHEDULING_EMAIL_TYPE,
} from "@miplatform/constants";
import { IContactCategoryResponse, IEmailRecipient } from "@miplatform/types";
import { mdiMinus, mdiPlus } from "@mdi/js";
import { useContactCategories } from "@mibuildweb/hooks";
import { Box, Typography } from "@mui/material";
import { ConfigAccordionHeader } from "../../components/ConfigAccordionHeader/ConfigAccordionHeader";
import {
  IEmailTemplateForm,
  IEmailTemplateFormItem,
} from "../ConfigOrderTemplates.types";

export const OrderEmailTemplateTabView: FC<OrderEmailTemplateTabViewProps> = ({
  extraTabProps: { emailForm, isDisabled, module } = {},
}) => {
  const { control, watch, setValue } =
    emailForm as UseFormReturn<IEmailTemplateForm>;
  const { fields, remove } = useFieldArray({
    control,
    name: "emailTemplates",
  });

  const { classes, theme } = useStyles();
  const { getContactCategories, contactCategories } = useContactCategories();

  useEffect(() => {
    getContactCategories("homes");
  }, []);

  const emailTemplates = watch("emailTemplates") || [];

  const emailTemplateFields = (index: number) => {
    const selectedEmailTypes = emailTemplates?.map(
      (template: IEmailTemplateFormItem, i: number) =>
        i !== index ? template.emailType : null
    );

    const emailTypeByModule =
      module === PLATFORM_MODULES.MI_DISPATCH
        ? DISPATCH_EMAIL_TYPE
        : SCHEDULING_EMAIL_TYPE;

    return [
      {
        label: "Email Template",
        name: `emailTemplates.${index}.emailType`,
        fieldType: INPUT_TYPES.SELECT,
        formControl: control,
        width: 400,
        padding: 0,
        disabled: isDisabled,
        options: Object.keys(emailTypeByModule)
          .filter((type) => !selectedEmailTypes?.includes(type))
          .map((type) => ({
            label: emailTypeByModule[type as keyof typeof emailTypeByModule],
            value: type,
          })),
      },
      {
        label: "Reply To",
        name: `emailTemplates.${index}.replyTo`,
        formControl: control,
        width: 400,
      },
    ];
  };

  const handleAddTemplate = () => {
    setValue(
      "emailTemplates",
      [...emailTemplates, { emailType: "", replyTo: "", to: [], cc: [] }],
      { shouldDirty: false, shouldValidate: false }
    );
  };

  return (
    <ConfigAccordionHeader title="Email Templates">
      {fields.length === 0 && (
        <MiPlaceholderArea
          text={`No ${ENTITY_NAMES.EMAIL_TEMPLATES}`}
          helperText={`Click the button below to add email templates for this ${ENTITY_NAMES.ORDER_TEMPLATE}.`}
          bgColor={theme.palette.primary.light}
        />
      )}
      {fields.map((field, index) => {
        return (
          <MiBox className={classes.miBox} key={field.id}>
            <Box className={classes.flexContainer}>
              <MiDetailFields
                detailFields={emailTemplateFields(index)}
                customWrapperStyle={{ display: "flex", alignItems: "center" }}
              />
              <MiButton
                title="Remove"
                variant="outlined"
                onClick={() => remove(index)}
                disabled={isDisabled}
                className={classes.removeTemplateButton}
              />
            </Box>
            <RecipientSection
              control={control}
              watch={watch}
              setValue={setValue}
              templateIndex={index}
              type="to"
              contactCategories={contactCategories}
              isDisabled={isDisabled}
            />
            <RecipientSection
              control={control}
              watch={watch}
              setValue={setValue}
              templateIndex={index}
              type="cc"
              contactCategories={contactCategories}
              isDisabled={isDisabled}
            />
          </MiBox>
        );
      })}
      <MiButton
        title="Add Template"
        variant="outlined"
        startIcon={{ path: mdiPlus }}
        onClick={handleAddTemplate}
        className={classes.btnMarginTop}
        disabled={isDisabled}
      />
    </ConfigAccordionHeader>
  );
};

const RecipientSection: FC<RecipientSectionProps> = ({
  control,
  watch,
  setValue,
  templateIndex,
  type,
  contactCategories,
  isDisabled,
}) => {
  const fieldName = `emailTemplates.${templateIndex}.${type}` as const;
  const sectionTitle = type.toUpperCase();
  const fieldLabel = `${sectionTitle}:`;
  const placeholderText = `No ${sectionTitle} Recipients`;
  const helperText = `Choose who will be ${
    type === "to" ? "sent the emails" : "CC'd on emails"
  } generated from this email template`;
  const addBtnTitle = `Add ${sectionTitle}`;

  const { fields, remove } = useFieldArray({
    control,
    name: fieldName,
  });

  const { classes, theme } = useStyles();

  const recipientValues = watch(fieldName);
  const emailType = watch(`emailTemplates.${templateIndex}.emailType`);

  const handleAppendRecipient = () => {
    if (!recipientValues) return;
    setValue(
      fieldName,
      [
        ...recipientValues,
        { type: "", relationshipType: "", emailAddress: "" },
      ],
      {
        shouldDirty: false,
        shouldValidate: false,
      }
    );
  };

  const recipientFields = (index: number) => {
    const recipientType = recipientValues?.[index]?.type;

    const selectedTypes = recipientValues?.map(
      (recipient: IEmailRecipient, i: number) =>
        i !== index &&
        (recipient?.type === "Assignee" || recipient?.type === "User")
          ? recipient?.type
          : null
    );

    const fields = [
      {
        label: fieldLabel,
        name: `${fieldName}.${index}.type`,
        fieldType: INPUT_TYPES.SELECT,
        required: true,
        formControl: control,
        padding: 0,
        disabled: isDisabled,
        options: RECIPIENT_TYPE_OPTIONS.filter(
          (option) =>
            option.value === "Contact" ||
            option.value === "Email" ||
            !selectedTypes?.includes(option.value as "Assignee" | "User" | null)
        ),
        setValue: (e: React.ChangeEvent<HTMLSelectElement>) => {
          setValue(`${fieldName}.${index}.type`, e.target.value, {
            shouldValidate: true,
            shouldDirty: true,
          });
          setValue(`${fieldName}.${index}.relationshipType`, "", {
            shouldValidate: true,
            shouldDirty: true,
          });
          setValue(`${fieldName}.${index}.emailAddress`, "", {
            shouldValidate: true,
            shouldDirty: true,
          });
        },
      },
    ];

    if (recipientType === "Contact") {
      fields.push({
        label: "Relationship Type",
        name: `${fieldName}.${index}.relationshipType`,
        fieldType: INPUT_TYPES.SELECT,
        formControl: control,
        padding: 0,
        required: true,
        disabled: isDisabled,
        options: contactCategories.map((category) => ({
          label: category.label,
          value: category.label,
        })),
      } as any);
    } else if (recipientType === "Email") {
      fields.push({
        label: "Email",
        name: `${fieldName}.${index}.emailAddress`,
        formControl: control,
        padding: 0,
        required: true,
        disabled: isDisabled,
      } as any);
    } else {
      fields.push({
        fieldType: INPUT_TYPES.CUSTOM,
        customComponent: <Box />,
      } as any);
    }

    return fields;
  };

  return (
    <Box className={classes.sectionBox}>
      <Typography className={classes.boldText}>{sectionTitle}</Typography>
      {fields.length > 0 ? (
        <Box className={classes.fieldsContainer}>
          {fields.map((field, index) => (
            <Box key={field.id} className={classes.fieldRow}>
              <Box sx={{ width: "100%" }}>
                <MiDetailFields
                  detailFields={recipientFields(index)}
                  customWrapperStyle={{ gap: 4 }}
                />
              </Box>
              <MiButton
                onClick={() => remove(index)}
                startIcon={{ path: mdiMinus }}
                className={classes.removeBtn}
                color={BUTTON_COLORS.RED}
                variant={BUTTON_VARIANTS.CONTAINED}
                disabled={isDisabled}
              />
            </Box>
          ))}
        </Box>
      ) : (
        <MiPlaceholderArea
          text={placeholderText}
          helperText={helperText}
          buttons={[
            {
              title: addBtnTitle,
              variant: BUTTON_VARIANTS.CONTAINED,
              onClick: handleAppendRecipient,
              startIcon: mdiPlus,
              disabled: !emailType || isDisabled,
            },
          ]}
          bgColor={theme.palette.common.white}
        />
      )}
      {fields.length > 0 && (
        <MiButton
          startIcon={{ path: mdiPlus }}
          onClick={handleAppendRecipient}
          size="small"
          title={addBtnTitle}
          variant={BUTTON_VARIANTS.OUTLINED}
          className={classes.btnMarginTop}
          disabled={isDisabled}
        />
      )}
    </Box>
  );
};

const useStyles = makeStyles()((theme) => ({
  miBox: {
    margin: theme.spacing(2, 0),
    backgroundColor: theme.palette.grey.lightAccent,
  },
  flexContainer: {
    display: "flex",
    width: "100%",
    justifyContent: "space-between",
    alignItems: "center",
  },
  sectionBox: {
    padding: theme.spacing(2),
    marginTop: theme.spacing(2),
    borderRadius: theme.spacing(2),
    background: theme.palette.common.white,
  },
  boldText: {
    fontWeight: theme.fontWeight.bold,
  },
  fieldsContainer: {
    marginTop: theme.spacing(2),
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
  },
  fieldRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  removeBtn: {
    borderRadius: "50%",
    padding: theme.spacing(0),
    marginLeft: 4,
  },
  removeTemplateButton: {
    height: 36,
    border: `1.5px solid ${theme.palette.primary.main}`,
  },
  btnMarginTop: {
    marginTop: theme.spacing(2),
    border: `1.5px solid ${theme.palette.primary.main}`,
  },
}));

interface OrderEmailTemplateTabViewProps {
  extraTabProps?: {
    emailForm?: UseFormReturn<IEmailTemplateForm>;
    isDisabled?: boolean;
    module?: string;
  };
}

interface RecipientSectionProps {
  control: UseFormReturn<IEmailTemplateForm>["control"];
  watch: UseFormReturn<IEmailTemplateForm>["watch"];
  setValue: UseFormReturn<IEmailTemplateForm>["setValue"];
  templateIndex: number;
  type: "to" | "cc";
  contactCategories: IContactCategoryResponse[];
  isDisabled?: boolean;
}
